import { OptimizationVisit } from 'bindings/optimization/OptimizationVisit';
import { VisitPause } from 'bindings/optimization/VisitPause';
import { VisitsOptimizationBody } from 'bindings/optimization/VisitsOptimizationBody';
import closeImage from 'images/icons/orders/close.svg';
import * as moment from 'moment';
import * as React from 'react';
import { getTranslate } from 'react-localize-redux';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { AEventStatusFilter, AEventTypeFilter } from '../../atoms/filter/eventFiltersAtom';
import { ATagFilter } from '../../atoms/filter/tagsFilterAtom';
import { AUserFilter } from '../../atoms/filter/usersFilterAtom';
import { AEventTypes } from '../../atoms/global/events';
import { AUsers } from '../../atoms/global/users';
import { AtomCategory } from '../../atoms/utils/model/Model';
import Add from '../../components_v2/add/Add';
import PageLoader from '../../components_v2/pageLoader/PageLoader';
import { PanelContext } from '../../components_v2/panel/context';
import Splitter from '../../components_v2/splitter/Splitter';
import ToolbarFilter, { ToolbarElement } from '../../components_v2/toolbarFilter/ToolbarFilter';
import { createRandomColorPerUser, momentToNaiveDateTime, useWindowDimensions } from '../../components_v2/utils';
import storeLang from '../../helpers/storeLang';
import { ViewSwitch, viewType } from '../../myToolbar/ViewSwitch';
import { AiButton } from '../../styles/global/css/GlobalButton';
import { PrimaryColor, DarkGreySidely2, SidelyBlack, SurfaceColor } from '../../styles/global/css/Utils';
import { Translate } from '../../styles/global/translate';
import { useFunctionState } from '../../utils/customHooks';
import useAlert from '../alert/UseAlert';
import CompanyListingPopup from '../client-companies/popup/CompanyListingPopup';
import { InstancePopup } from '../forms/formInstancePopup';
import { ToolbarBox } from '../globals/defaultToolbar/style/Style';
import { ToolbarState } from '../globals/mainPage/mainPage';
import { LoadingStateEnum } from '../import/model';
import { CalendarMapView, useTripFilter } from '../map/calendarMapView';
import { MapButton, NullableReactStateType, ReactStateType } from '../map/Map';
import { ComponentLoader, OptimizationInformations, OptimizePopup } from '../map/modalRight/ModalCalendar';
import { Interconnection, MapEventOptions, OptimizeCompany } from '../map/modalRight/model';
import { Unhautorized } from '../noData/NoData';
import Restricted from '../permissions/Restricted';
import { ModalState } from '../products/model';
import { FlexDiv } from '../products/style';
import PopupPromotion from '../promotions/PopupPromotion';
import SidelyCalendar, { CalendarEvent, CalendarEvents, EventsBoundsForMapView, EventsTypes } from './Calendar';
import CalendarList, { CalendarEventUpdater } from './CalendarList';
import { optimizeVisits, putEvents, PutEventsBody } from './EventsApi';
import ModalLeft from './ModalLeft';
import { ModalLeftToggle } from '../map/modalLeft/ModalLeftFilter';
import { RepresentationMode } from './model/Model';
import PopupEvent from './popup/Event';
import { NewEventDefaultValues } from './popup/NewEvent';
import { TOOLBARS_HEIGHT } from '../globals/defaultToolbar/style/Style';

type loadingState = 'loading' | 'loaded' | 'error' | null

export function initialCalendarFilters(changed: boolean) {
	return {
		types: [EventsTypes.Event, EventsTypes.Promotion],
		changed
	};
}

const CloseMapButton = styled(MapButton) <{ url: string }>`
	background-image: url(${p => p.url});
	background-position: center;
	background-repeat: no-repeat;
	background-size: 25px;
`;

const AnimatedMapButton = styled(MapButton) <{ top?: number }>`
	padding: 5px;

	.speed-bar-left {
		transition: width 1s;
	}

	.speed-bar-right {
		transition: width 1s;
	}

	.speed-contoler {
		transition: 1s;
		transform-origin: 50% calc(50% - -3px);
		rotate: -90deg;
	}

	&:hover {
		.speed-contoler {
			rotate: 0deg;
		}

		.speed-bar-left {
			width: 4.5px;
		}

		.speed-bar-right {
			width: 1px;
		}

		.speed-bar-middle {
			animation: grow-then-shrink 1s;
		}
	}

	@keyframes grow-then-shrink {
		0%, 100% {
			width: 4.5px;
		}
		50% {
			width: 2.75px;
		}
	}
`;

const perUserEventStyle = (colors: Map<number, string>) => (event: CalendarEvents, hovered: boolean) => {
	if (event.type === EventsTypes.Promotion) {
		const filter = hovered ? 'brightness(50%)' : '';
		return {
			style: {
				backgroundColor: SurfaceColor,
				border: `1px solid ${event.color ?? PrimaryColor}`,
				padding: 0,
				color: SidelyBlack,
				filter
			}
		};
	}
	let incharge: number | undefined = undefined;
	if ('incharge' in event) {
		incharge = event.incharge;
	} else if ('checkIn' in event) {
		incharge = event.checkIn.user_id;
	} else if ('owner_id' in event) {
		incharge = event.owner_id;
	}
	return {
		style: {
			backgroundColor: colors.get(incharge ?? -1) ?? PrimaryColor,
			color: 'white',
			filter: `brightness(${hovered ? 80 : 100}%)`
		}
	};
};

export type CalendarViewState = {
	eventsHook: ReactStateType<CalendarEvents[]>,
	resetHook: ReactStateType<boolean>,
	dateHook: ReactStateType<Date>,
	mapModalHook: NullableReactStateType<EventsBoundsForMapView>,
	eventModalHook: ReactStateType<ModalState<{ event?: CalendarEvent, defaultValues?: NewEventDefaultValues }>>,
	hoveredEventHook: NullableReactStateType<HoveredEvent>,
}

export type HoveredEvent = { id: string, companyId?: number, fromCalendar?: boolean }

export default function CalendarView(props: {
	setToolBarState?: (value: ToolbarState) => void,
	panelView?: boolean,
	splitterPadding?: number
}) {
	const { setDisabled, calendarViewState } = React.useContext(PanelContext);
	const s = props.panelView ? calendarViewState : undefined;
	const [isModalOpen, setIsModalOpen] = React.useState<boolean>(true);
	const translate = getTranslate(storeLang.getState().localize);
	const { height } = useWindowDimensions();
	const [loadingState, setLoadingState] = React.useState<loadingState>(null);
	const [optimizationLoadingState, setOptimizationLoadingState] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADED);
	const [rawEventsCalendar, setRawCalendar] = s?.eventsHook ?? React.useState<CalendarViewState['eventsHook'][0]>([]);
	const localStorageEventFiltersStatus = localStorage.getItem('eventFiltersStatus');
	const [filters, setFilters] = React.useState<object>(() => ({
		...(localStorageEventFiltersStatus ? { changed: false, types: JSON.parse(localStorageEventFiltersStatus) } : initialCalendarFilters(false)),
		includeDrafts: localStorage.getItem('calendarIncludeDrafts') === 'true',
	}));
	// unfinished instances (progression < 100) join the calendar on demand;
	// the flag lives in `filters` so changing it re-fetches
	const updateIncludeDrafts = React.useCallback((value: boolean) => {
		localStorage.setItem('calendarIncludeDrafts', String(value));
		setFilters(prev => ({ ...prev, includeDrafts: value }));
	}, []);
	// forms / chunk labels the user unchecked in the filter panel; applied at
	// render only (never refetches) and remembered like the type filters
	const [hiddenForms, setHiddenForms] = React.useState<number[]>(JSON.parse(localStorage.getItem('calendarHiddenForms') ?? '[]'));
	const [hiddenBounds, setHiddenBounds] = React.useState<string[]>(JSON.parse(localStorage.getItem('calendarHiddenBounds') ?? '[]'));
	const updateHiddenForms = (value: number[]) => {
		localStorage.setItem('calendarHiddenForms', JSON.stringify(value));
		setHiddenForms(value);
	};
	const updateHiddenBounds = (value: string[]) => {
		localStorage.setItem('calendarHiddenBounds', JSON.stringify(value));
		setHiddenBounds(value);
	};
	// what the fetched window actually holds, offered as filter options
	const formFilterOptions = React.useMemo(() => {
		const forms = new Map<number, string>();
		const bounds = new Set<string>();
		rawEventsCalendar.forEach(e => {
			if (e.type !== EventsTypes.Form) return;
			// a backend older than these fields sends rows without them: skip
			// them rather than listing a phantom unnamed entry
			if (typeof e.form_id !== 'number') return;
			forms.set(e.form_id, e.form_name || `#${e.form_id}`);
			if (typeof e.label === 'string') bounds.add(e.label);
		});
		return {
			forms: [...forms].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)),
			bounds: [...bounds].sort(),
		};
	}, [rawEventsCalendar]);
	const [reset, setReset] = s?.resetHook ?? useFunctionState<CalendarViewState['resetHook'][0]>(false, undefined, (reset) => calendarViewState.resetHook[1](reset));
	const [view, setView] = React.useState<viewType>('calendar');
	const [modalState, setModalState] = s?.eventModalHook ?? React.useState<CalendarViewState['eventModalHook'][0]>({ isOpen: false });
	const [promotionModalState, setPromotionModalState] = React.useState<ModalState<number>>({ isOpen: false });
	const [formModalState, setFormModalState] = React.useState<ModalState<string>>({ isOpen: false, fullOpenMode: false });
	const [representationMode, setRepresentationMode] = React.useState<RepresentationMode>();
	const owners = useRecoilValue(AUsers);
	const [colors, colorsMap] = React.useMemo(() => {
		const colors = createRandomColorPerUser(owners);
		return [colors, new Map(colors.map((color, index) => [owners[index].id, color.color]))];
	}, []);
	const [hoveredEvent, setHoveredEvent] = s?.hoveredEventHook ?? React.useState<CalendarViewState['hoveredEventHook'][0]>();
	const [mapModalState, setMapModalState] = s?.mapModalHook ?? React.useState<CalendarViewState['mapModalHook'][0]>();
	const [date, setDate] = s?.dateHook ?? React.useState<CalendarViewState['dateHook'][0]>(new Date());
	const atomEventTypes = useRecoilValue(AEventTypes);
	const mapTime = React.useMemo(() => moment(date), [date]);
	// While the map is open, each day it draws gets its trip colour in the
	// calendar header; clicking the dot hides that trip, as does its entry in
	// the map's legend: both read the same hidden set.
	const mapGranularity = mapModalState?.granularity;
	const tripFilter = useTripFilter(mapGranularity ?? 'day');
	const dayDotColor = React.useCallback((day: Date, hasMapEntries: boolean) => {
		if (!mapGranularity || !hasMapEntries) return undefined;
		if (!moment(day).isSame(date, mapGranularity as moment.unitOfTime.StartOf)) return undefined;
		return tripFilter.dotColor(day);
	}, [mapGranularity, date, tripFilter.dotColor]);
	const [modalStateOptimize, setModalStateOptimize] = React.useState<ModalState<OptimizeCompany[]>>({ isOpen: false });
	const alert = useAlert();

	const companyListingPopupOpenState = React.useState({ isOpen: false });
	const [_, setCompanyListingPopupOpenState] = companyListingPopupOpenState;


	React.useEffect(() => {
		if (!props.panelView) {
			setDisabled(true);
		}
		return () => {
			setDisabled(false);
		};
	}, []);

	React.useEffect(() => {
		if (view !== 'list') {
			props.setToolBarState?.({
				title: translate('calendar.title').toString(),
				bottomLeftToolbarComponent: <ToolbarBox>
					<ModalLeftToggle isOpen={isModalOpen} onClick={() => setIsModalOpen(open => !open)} />
					<ToolbarFilter
						permission='ReadEvent'
						category={AtomCategory.CALENDAR}
						elements={[
							{
								kind: ToolbarElement.USER_FILTER,
								user: 'me'
							},
							ToolbarElement.EVENT_STATUS_FILTER,
							ToolbarElement.EVENT_TYPE_FILTER,
							ToolbarElement.TAG_FILTER
						]}
					/>
					{loadingState == 'loading' && <div style={{ marginLeft: 5 }}><PageLoader size={20} /></div>}
				</ToolbarBox>,
				bottomRightToolbarComponent: <FlexDiv gap='10px'>
					<Restricted to={{ objectAction: 'ViewQuickTspOptimization' }}>
						<AiButton
							margin='0'
							onClick={() => setCompanyListingPopupOpenState({ isOpen: true })}
						>
							<Translate id='map.modal_right.optimize' />
						</AiButton>
					</Restricted>
					<ViewSwitch dispatch={res => setView(res.data)} value={view} />
					<Restricted to={{ objectAction: 'CreateEvent' }}>
						<Add size='22px' onClick={() => setModalState({ isOpen: true })} />
					</Restricted>
				</FlexDiv>
			});
		}
	}, [view, loadingState, isModalOpen]);

	const filteredUsers = useRecoilValue(AUserFilter);
	const filteredStatuses = useRecoilValue(AEventStatusFilter);
	const filteredTypes = useRecoilValue(AEventTypeFilter);
	const filteredTags = useRecoilValue(ATagFilter);

	// force update
	function refreshData() {
		setReset(true);
	}

	// open or close modal
	function toggleModal() {
		setModalState({
			isOpen: !modalState.isOpen,
		});
	}

	function handleFilter(key: string, value) {
		if (key === 'reset') {
			setFilters(initialCalendarFilters(true));
			return;
		}
		const newFilters = filters;
		newFilters[key] = value;
		setFilters({ ...newFilters });
	}

	function onSelectSlot(e) {
		setModalState({
			...modalState,
			isOpen: true,
			data: {
				defaultValues: {
					start: new Date(e.start),
					end: new Date(e.end)
				}
			}
		});
	}

	let eventStyle: undefined | ((event: CalendarEvents, hovered: boolean) => ({ style }));

	switch (representationMode) {
		case 'per_users':
			eventStyle = perUserEventStyle(colorsMap);
			break;
	}

	const mapEvents = React.useMemo(() => rawEventsCalendar.reduce((acc: MapEventOptions[], e): MapEventOptions[] => {
		switch (e.type) {
			case EventsTypes.CheckIn:
				if (!acc[1]) {
					acc[1] = { events: [], lineColor: SidelyBlack };
				}
				acc[1].events.push({
					companyId: e.checkIn.client_company_id,
					eventId: e.id,
					eventName: 'Check In' + e.checkIn.company_name ? ' -- ' + e.checkIn.company_name : '',
					companyName: e.checkIn.company_name ?? '',
					lat: e.checkIn.latitude,
					lng: e.checkIn.longitude,
					time: moment(e.start)
				});
				break;
			case EventsTypes.Event: {
				const baseEvent = atomEventTypes.find(ae => ae.id === e.eventTypeId);
				if (e.allDay) return acc;
				if (baseEvent?.map && e.latitude && e.longitude && e.companieName) {
					if (!acc[0]) {
						acc[0] = { events: [], lineColor: baseEvent.color, eventType: baseEvent.id };
					}
					acc[0].events.push({
						companyId: e.clientCompanyId ?? -1,
						eventId: e.id,
						eventName: e.title,
						companyName: e.companieName,
						lat: e.latitude,
						lng: e.longitude,
						time: moment(e.start),
						color: baseEvent.color
					});
				}
			}
		}
		return acc;
	}, []), [rawEventsCalendar]);

	const svg = <svg id="speed-container" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 46 46" shapeRendering="geometricPrecision" textRendering="geometricPrecision">
		<path className="speed-contoler" id="speed-contoler" d="M31.6308,16.1029l-7.1765,7.055c-.7741-.3729-1.6551-.4602-2.4874-.2464s-1.5621.715-2.0606,1.4149-.7336,1.5535-.6636,2.4099.4403,1.6605,1.0458,2.2703s1.4069.9858,2.2628,1.0618s1.7111-.153,2.4145-.6466s1.2097-1.2199,1.4294-2.0506.1387-1.7123-.2287-2.4891l7.1886-7.055-1.7243-1.7242Z" fill={DarkGreySidely2} /><path d="M22.9126,6.3036c-3.8217-.00438-7.5649,1.08501-10.7874,3.1395s-5.78972,4.9883-7.39852,8.4549-2.19195,7.3211-1.68058,11.1084s2.0959,7.3493,4.56645,10.2651l.36429.425h29.87146l.3643-.425c2.4705-2.9158,4.055-6.4777,4.5664-10.2651s-.0718-7.6419-1.6806-11.1084-4.176-6.4004-7.3985-8.4549-6.9656-3.14388-10.7873-3.1395ZM11.2918,13.1279c2.894-2.5579,6.5529-4.08634,10.4065-4.34716v.01214h2.4285v0c4.2567.3087,8.2555,2.15612,11.2497,5.19732c2.9943,3.0412,4.7793,7.0682,5.0218,11.3291h-4.2257v2.4286h4.2864c-.2826,3.4786-1.5976,6.7936-3.7764,9.52h-27.54005c-2.17882-2.7264-3.49381-6.0414-3.77643-9.52h4.18929v-2.4286h-4.18929c.20336-3.8557,1.67628-7.5358,4.18929-10.4671" fill={DarkGreySidely2} />
		<rect className="speed-bar-left" id="speed-bar-left" width="1" height="2.43" rx="0" ry="0" transform="matrix(.693577 0.720383-.720383 0.693577 11.232456 12.997996)" fill={DarkGreySidely2} strokeWidth="0" />
		<rect className="speed-bar-middle" id="speed-bar-middle" width="4.5" height="2.43" rx="0" ry="0" transform="matrix(-.014999 0.999888-.999888-.014999 24.16118 8.504911)" fill={DarkGreySidely2} strokeWidth="0" />
		<rect className="speed-bar-right" id="speed-bar-right" width="4.5" height="2.43" rx="0" ry="0" transform="matrix(-.720383 0.693577 0.693577 0.720383 35.061367 12.967957)" fill={DarkGreySidely2} strokeWidth="0" />
	</svg>;

	const onOptimize = (_ids: string[], interconnections: Interconnection[], infos: OptimizationInformations) => {
		setOptimizationLoadingState(LoadingStateEnum.LOADING);
		const start = moment(date).set({ hour: infos.start.getHours(), minute: infos.start.getMinutes(), second: 0, millisecond: 0 });
		const visits = interconnections.reduce<OptimizationVisit[]>((acc, interconnection) => {
			const event = rawEventsCalendar.find(e => 'id' in e && e.id.toString() == interconnection.toWaypoint);
			if (!event || !('id' in event)) return acc;
			if (event.type === EventsTypes.Event && event.latitude && event.longitude) {
				acc.push({
					travel_duration: interconnection.time,
					id: parseInt(event.id.toString()),
					duration: (event.end.getTime() - event.start.getTime()) / 1000,
					lat: event.latitude,
					lon: event.longitude
				});
			}
			return acc;
		}, []);

		const pause: VisitPause | null = infos.pause ? {
			time: momentToNaiveDateTime(moment(infos.pause.time)),
			duration: infos.pause.duration_sec
		} : null;
		const body: VisitsOptimizationBody = {
			visits,
			first_visit: momentToNaiveDateTime(start),
			working_hours: {
				start: momentToNaiveDateTime(start),
				end: momentToNaiveDateTime(moment(infos.end))
			},
			pause,
			working_days: [],
			max_event_day: null,
			constraints: null
		};
		optimizeVisits(body)
			.then(sorted_events => {
				let overlapedEvents = 0;
				const events: PutEventsBody[] = sorted_events.map(se => {
					if (moment(se.start).format('LL') !== start.format('LL')) {
						overlapedEvents += 1;
						return [se.id, { start: moment(moment(se.start).format('YYYY-MM-DD 00:00:00')).utc().format('YYYY-MM-DD HH:mm:ss'), end: moment(moment(se.end).format('YYYY-MM-DD 23:59:00')).utc().format('YYYY-MM-DD HH:mm:ss') }];
					}
					return [se.id, { start: moment(se.start).utc().format('YYYY-MM-DD HH:mm:ss'), end: moment(se.end).utc().format('YYYY-MM-DD HH:mm:ss') }];
				});
				if (overlapedEvents) {
					setOptimizationLoadingState(LoadingStateEnum.LOADED);
					alert({
						title: 'Attention',
						content: translate('{{NUMBER}}_events_exceed_the_day').toString().replace('{{NUMBER}}', overlapedEvents.toString()),
						svg: 'warning',
						noButtons: true
					});
					return;
				}
				putEvents(events)
					.then(_ => {
						refreshData();
						setOptimizationLoadingState(LoadingStateEnum.LOADED);
					})
					.catch(e => {
						console.error(e);
						setOptimizationLoadingState(LoadingStateEnum.ERROR);
					});
			})
			.catch(e => {
				console.error(e);
				setOptimizationLoadingState(LoadingStateEnum.ERROR);
			});
	};

	return (
		<Restricted to={{ objectAction: 'ViewCalendar' }} fallback={<Unhautorized />}>
			{/* initialize filters if panelView */}
			{props.panelView && <div style={{ display: 'none' }}>
				<ToolbarFilter
					permission='ReadEvent'
					category={AtomCategory.CALENDAR}
					elements={[
						{
							kind: ToolbarElement.USER_FILTER,
							user: 'me'
						},
						ToolbarElement.EVENT_STATUS_FILTER,
						ToolbarElement.EVENT_TYPE_FILTER,
						ToolbarElement.TAG_FILTER
					]}
				/>
			</div>}
			<div style={{ height: '100%', marginLeft: isModalOpen && view === 'calendar' && !props.panelView ? 275 : 0, transition: 'margin-left 0.25s' }}>
				{!loadingState && view === 'calendar' &&
					<div className="list-loader">
						<PageLoader />
					</div>
				}
				{view === 'list' && <CalendarList
					onSelectEvent={event => setModalState({ data: { event }, isOpen: true })}
					setToolBarState={props.setToolBarState}
					setView={setView}
					refreshData={refreshData}
				/>}
				{view === 'calendar' && <>
					{/* @ts-expect-error I assure, this is working */}
					<Splitter
						style={{ height: props.panelView ? '100%' : height - TOOLBARS_HEIGHT - 10 }}
						direction="vertical"
						sizes={mapModalState !== undefined ? [66, 33] : [100]}
						ishidden={(mapModalState === undefined).toString()}
						minSize={0}
					>
						<div style={{ overflow: 'auto', height: mapModalState !== undefined ? undefined : '100%', position: 'relative' }} className={view !== 'calendar' ? '' : 'table-bg'}>
							<SidelyCalendar
								isMapOpen={mapModalState !== undefined}
								events={rawEventsCalendar}
								setEvents={setRawCalendar}
								hiddenForms={hiddenForms}
								hiddenBounds={hiddenBounds}
								onSelectSlot={onSelectSlot}
								onSelectEvent={(event: CalendarEvents) => {
									switch (event.type) {
										case EventsTypes.Event:
											setModalState({ data: { event }, isOpen: true });
											break;
										case EventsTypes.ShelfAudit:
											break;
										case EventsTypes.FreeForm:
											window.open(`/formBuilder/formData/${event.link}`);
											break;
										case EventsTypes.Order:
											window.open(`/orders?id=${event.id}`);
											break;
										case EventsTypes.CheckIn:
											window.open(`/companies?id=${event.checkIn.client_company_id}`);
											break;
										case EventsTypes.Promotion:
											setPromotionModalState({ data: event.promotion_id, isOpen: true });
											break;
										case EventsTypes.Form:
											setFormModalState({ isOpen: true, data: event.uuid, fullOpenMode: true });
											break;

									}
								}}
								column={props.panelView}
								filters={filters}
								toolBarState={{
									userId: filteredUsers,
									statusId: filteredStatuses,
									typeId: filteredTypes,
									tags: filteredTags
								}}
								setLoadingState={(e) => {
									return setLoadingState(e);
								}}
								loadingState={loadingState}
								reset={reset}
								setReset={setReset}
								style={{ width: 'auto', margin: 'auto' }}
								eventStyle={eventStyle}
								onEventHover={event => {
									if (!event || !('id' in event)) return setHoveredEvent(undefined);
									let companyId: number | undefined;
									if ('clientCompanyId' in event) companyId = event.clientCompanyId;
									else if ('companyId' in event) companyId = event.companyId;
									setHoveredEvent({ id: event.id.toString(), companyId, fromCalendar: true });
								}}
								hoveredEvent={hoveredEvent}
								onMapClicked={setMapModalState}
								date={date}
								onDateChange={setDate}
								defaultView={props.panelView ? 'day' : undefined}
								mapGranularity={mapModalState?.granularity}
								dayDotColor={dayDotColor}
								onDayDotClick={tripFilter.toggle}
							/>
							{props.panelView && <ComponentLoader loadingState={loadingState == 'loading' ? LoadingStateEnum.LOADING : LoadingStateEnum.LOADED} zIndex={'4'} />}
						</div>
						<div style={{ height: mapModalState !== undefined ? undefined : 0, overflow: 'hidden' }}>
							<Restricted
								to={{ objectAction: 'ViewMap' }}
								fallback={<div style={{ position: 'relative', height: '100%' }}>
									<CloseMapButton onClick={() => setMapModalState(undefined)} url={closeImage} />
									<Unhautorized height='100%' />
								</div>}
							>
								{mapModalState !== undefined && <>
									<CalendarMapView
										drawRoute={!!filteredUsers}
										topRightButtons={<>
											<CloseMapButton onClick={() => setMapModalState(undefined)} url={closeImage} />
											<AnimatedMapButton onClick={_ => setModalStateOptimize({
												isOpen: true,
												data: mapEvents.filter(me => atomEventTypes.some(ae => ae.id === me.eventType && ae.map))
													?.[0]
													?.events
													?.filter(e => e.time.format('LL') == moment(date).format('LL'))
													?.map((e): OptimizeCompany => ({
														id: parseInt(`${e.eventId}`),
														lat: e.lat,
														lng: e.lng
													}))
											})
											} top={50}>
												{svg}
											</AnimatedMapButton>

										</>}
										useGranularity
										height='100%'
										mapEvents={mapEvents}
										hoveredEvent={hoveredEvent}
										setHoveredEvent={setHoveredEvent}
										granularity={mapModalState.granularity}
										time={mapTime}
										hiddenTrips={tripFilter.hidden}
										onToggleTrip={tripFilter.toggle}
									/>
								</>}
							</Restricted>
						</div>
					</Splitter>
					<ComponentLoader height={`${height}px`} loadingState={optimizationLoadingState} allScreen zIndex='10' />
				</>}
				<PopupEvent
					isOpen={modalState.isOpen}
					setIsOpen={toggleModal}
					onDelete={refreshData}
					onCreate={refreshData}
					onValueChange={modalState.data?.event?.id ? (values, id) => CalendarEventUpdater(values, id, rawEventsCalendar, setRawCalendar) : undefined}
					event={modalState.data?.event}
					defaultValues={modalState.data?.defaultValues}
				/>
				<OptimizePopup
					companies={modalStateOptimize.data}
					errorCompanies={[]}
					onOptimize={onOptimize}
					isOpen={modalStateOptimize.isOpen}
					setIsOpen={() => setModalStateOptimize({ isOpen: false })}
					allInformations
				/>
				<PopupPromotion
					setIsOpen={(isOpen: boolean) => setPromotionModalState({ isOpen })}
					isOpen={promotionModalState.isOpen}
					promotionId={promotionModalState.data}
					onDelete={refreshData}
				/>
				<InstancePopup modalState={formModalState} isOpen={formModalState.isOpen}
					onClickOut={() => setFormModalState({ ...formModalState, isOpen: false, })} />

				<div className={view !== 'calendar' ? 'd-none' : ''}>
					<ModalLeft isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} handleFilter={handleFilter} onRepresentationChange={setRepresentationMode} colors={colors} panelView={props.panelView} formFilterOptions={formFilterOptions} hiddenForms={hiddenForms} hiddenBounds={hiddenBounds} onHiddenFormsChange={updateHiddenForms} onHiddenBoundsChange={updateHiddenBounds} includeDrafts={'includeDrafts' in filters ? !!filters.includeDrafts : false} onIncludeDraftsChange={updateIncludeDrafts} />
				</div>
			</div>
			<CompanyListingPopup modalStateHook={companyListingPopupOpenState} onEventCreate={() => setReset(r => !r)} order_bys={[]} />
		</Restricted>
	);
}
